import { FC } from 'react';

import img2 from '../../../assets/images/terminate-stakeholders-2.png';
import img1 from '../../../assets/images/terminate-stakeholders-guide.png';

export const StakeholderTermination: FC = () => (
  <>
    <div className="flex flex-col gap-2" id="heading">
      <span className="text-xl font-semibold text-gray-700">Step-by-Step Guide</span>
    </div>
    <div className="flex flex-col gap-2" id={`main-step-1`}>
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        Step 1: Select Stakeholders for Termination
      </h2>
      <ol className="flex list-decimal flex-col gap-2 pl-10">
        <li className="text-xs font-[450] text-gray-700">
          Navigate to the <strong>Stakeholders</strong> page.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          Use the actions near each stakeholder or checkboxes to select one or more stakeholders you
          wish to terminate
        </li>
        <li className="text-xs font-[450] text-gray-700">
          Click on <strong>Terminate Stakeholder</strong>.
        </li>
      </ol>
      <img src={img1} width={320} />
    </div>

    <div className="flex flex-col gap-2" id={`main-step-2`}>
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        Step 2: New Stakeholder Type
      </h2>
      <span className="text-xs font-[450] text-gray-700">
        Once you open the termination modal, the system will automatically detect and assign the{' '}
        <strong>New Type</strong> based on the stakeholder’s current role:
      </span>
      <ul className="flex list-disc flex-col gap-2 pl-10">
        <li className="text-xs font-bold text-gray-700">Employee → Ex-Employee</li>
        <li className="text-xs font-bold text-gray-700">Founder → Ex-Founder</li>
        <li className="text-xs font-bold text-gray-700">Advisor → Ex-Advisor</li>
      </ul>
    </div>

    <div className="flex flex-col gap-2" id={`main-step-3`}>
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        Step 3: Choose Termination Type
      </h2>
      <ol className="flex list-decimal flex-col gap-2 pl-10">
        <li className="text-xs font-[450] text-gray-700">
          <div className="flex flex-col gap-2">
            <span>
              In the <strong>"Termination Type"</strong> dropdown, select the reason why the
              stakeholder is leaving the company.
            </span>
            <span>The options include:</span>
            <ul className="flex list-disc flex-col gap-2 pl-10">
              <li className="text-xs font-bold text-gray-700">Resignation</li>
              <li className="text-xs font-bold text-gray-700">Termination</li>
              <li className="text-xs font-bold text-gray-700">Termination with Cause</li>
              <li className="text-xs font-bold text-gray-700">Retirement.</li>
            </ul>
          </div>
        </li>
        <li className="text-xs font-[450] text-gray-700">
          Each termination type might affect the amount of time to{' '}
          <strong>exercise a stakeholder’s vested shares</strong>.
        </li>
      </ol>
      <span className="text-xs font-bold text-gray-700">✔ Tip:</span>
      <ul className="flex list-disc flex-col gap-2 pl-10">
        <li className="text-xs font-[450] text-gray-700">
          If your stakeholder has <strong>exercisable grants</strong> (such as{' '}
          <em>Stock Options</em> or
          <em>Warrants</em>), selecting a termination type may automatically calculate how much you
          have to exercise their shares after leaving.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          This time window is called the <strong>Exercise Expiration Period</strong>, and it will be
          automatically calculated as the <strong>Final day to exercise</strong>.
        </li>
      </ul>
    </div>

    <div className="flex flex-col gap-2" id={`main-step-4`}>
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        Step 4: Set Termination Date
      </h2>
      <span className="text-xs font-[450] text-gray-700">
        Choose the date when the stakeholder officially leaves the company.
      </span>
      <span className="text-xs font-bold text-gray-700">✔ Note:</span>
      <span className="text-xs font-[450] text-gray-700">
        The termination date affects the final vesting calculations.
      </span>
    </div>

    <div className="flex flex-col gap-2" id={`main-step-5`}>
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        Step 5: Set Last Vesting Date
      </h2>
      <ol className="flex list-decimal flex-col gap-2 pl-10">
        <li className="text-xs font-[450] text-gray-700">
          The <strong>Last Vesting Date</strong> will be auto-filled based on grant schedules.
        </li>
        <li className="text-xs font-[450] text-gray-700">You can manually adjust it if needed.</li>
      </ol>
      <span className="text-xs font-bold text-gray-700">✔ Tip:</span>
      <span className="text-xs font-[450] text-gray-700">
        This date is used to calculate how much grants will be vested before termination.
      </span>
    </div>

    <div className="flex flex-col gap-2" id={`main-step-6`}>
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        Step 6: Review Grant Information
      </h2>
      <ol className="flex list-decimal flex-col gap-2 pl-10">
        <li className="text-xs font-[450] text-gray-700">
          Click the arrow next to a stakeholder to expand and view their grants.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          <div className="flex flex-col gap-2">
            Each grant will show the number of:
            <ul className="flex list-disc flex-col gap-2 pl-10">
              <li className="text-xs font-[450] text-gray-700">
                <strong>Granted, Vested, Unvested, Exercised,</strong> and{' '}
                <strong>Exercisable</strong> shares.
              </li>
              <li className="text-xs font-[450] text-gray-700">
                <strong>Final day to exercise</strong> and <strong>Days remaining</strong>.
              </li>
            </ul>
          </div>
        </li>
        <li className="text-xs font-[450] text-gray-700">
          Double-check these values before proceeding.
        </li>
      </ol>
    </div>

    <div className="flex flex-col gap-2" id={`main-step-7`}>
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        Step 7: Confirm Termination
      </h2>
      <ol className="flex list-decimal flex-col gap-2 pl-10">
        <li className="text-xs font-[450] text-gray-700">
          Tick the checkbox{' '}
          <strong>"I confirm that the remaining days to exercise are correct"</strong>.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          (Optional) Tick <strong>"Send email with termination details to stakeholders"</strong> if
          you want to notify them by email.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          Click the <strong>Terminate</strong> button to complete the process.
        </li>
      </ol>
      <img src={img2} width={720} />
    </div>

    <div className="flex flex-col gap-2" id="key-notes-and-tips">
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">Key Notes and Tips</h2>
      <ul className="flex list-disc flex-col gap-2 pl-10">
        <li className="text-xs font-bold text-gray-700">
          Termination doesn’t remove stakeholders — it simply updates their status.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          They will appear in the <strong>Terminated</strong> tab starting from the termination
          date; until then, they remain in the <strong>Stakeholders</strong> tab.
        </li>
      </ul>
    </div>
  </>
);
